import React, { useState, useEffect } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import { FiPlus, FiTrash2, FiImage, FiEdit2, FiSearch, FiFolder, FiCheck, FiX } from "react-icons/fi";
import { baseurl, imgurl } from "../baseurl";
import axios from "axios";
import Swal from "sweetalert2";
import { useDropzone } from "react-dropzone";

function CategoryBuilder() {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newCategoryImage, setNewCategoryImage] = useState(null);
  const [newSubcategory, setNewSubcategory] = useState({});
  const [editCategoryName, setEditCategoryName] = useState({});
  const [editCategoryImage, setEditCategoryImage] = useState({});
  const [editSubcategoryName, setEditSubcategoryName] = useState({});
  const [search, setSearch] = useState("");
  const [activeEditId, setActiveEditId] = useState(null);

  // -------------------- Toasts -------------------- //
  const showToast = (title, icon = "success") => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      title,
      showConfirmButton: false,
      timer: 2000,
      background: "#fff",
      color: "#333",
      customClass: {
        popup: 'shadow-sm border'
      }
    });
  };

  const ImageDropzone = ({ onFileSelect, preview, label = "Upload Image", height = "100px" }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: { "image/*": [] },
      multiple: false,
      onDrop: (acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
          onFileSelect(acceptedFiles[0]);
        }
      },
    });

    return (
      <div
        {...getRootProps()}
        className={`border rounded d-flex align-items-center justify-content-center flex-column bg-light transition-all ${isDragActive ? "border-primary bg-white" : "border-secondary"}`}
        style={{
          border: "2px dashed #cbd5e0",
          cursor: "pointer",
          height: height,
          transition: "0.2s ease",
          overflow: "hidden"
        }}
      >
        <input {...getInputProps()} />
        {preview ? (
          <img src={preview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div className="text-center p-2">
            <FiImage size={20} className="text-primary mb-1" />
            <p className="m-0 text-muted" style={{ fontSize: "11px" }}>{label}</p>
          </div>
        )}
      </div>
    );
  };

  // -------------------- Data Fetching -------------------- //
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${baseurl}getCategoriesWithSubcategories`);
      const formatted = res.data.categories.map((c) => ({
        id: c.id || c.category_id,
        name: c.name || c.category_name || "",
        image: c.image || c.category_image || "",
        subcategories: Array.isArray(c.subcategories)
          ? c.subcategories.map((s) => ({
            id: s.id,
            name: s.name || "",
          }))
          : [],
      }));
      setCategories(formatted);
    } catch (err) {
      console.error("Fetch categories error:", err);
      showToast("Failed to fetch categories", "error");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // -------------------- Logic (Unchanged) -------------------- //
  const addCategory = async () => {
    if (!newCategory.trim()) return showToast("Category name required", "error");
    try {
      const formData = new FormData();
      formData.append("name", newCategory.trim());
      if (newCategoryImage) formData.append("image", newCategoryImage);
      await axios.post(`${baseurl}addCategory`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showToast("Category added successfully");
      setNewCategory(""); setNewCategoryImage(null);
      fetchCategories();
    } catch (err) {
      showToast("Failed to add category", "error");
    }
  };

  const updateCategory = async (id) => {
    const updatedName = editCategoryName[id];
    const updatedImage = editCategoryImage[id];
    if (!updatedName && !updatedImage) return setActiveEditId(null);
    try {
      const formData = new FormData();
      if (updatedName) formData.append("name", updatedName.trim());
      if (updatedImage) formData.append("image", updatedImage);
      await axios.put(`${baseurl}updateCategory/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showToast("Category updated successfully");
      setEditCategoryName({ ...editCategoryName, [id]: "" });
      setEditCategoryImage({ ...editCategoryImage, [id]: null });
      setActiveEditId(null);
      fetchCategories();
    } catch (err) {
      showToast("Failed to update category", "error");
    }
  };

  const deleteCategory = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This will remove all linked subcategories!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: "Yes, delete it!",
    });
    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${baseurl}deleteCategory/${id}`);
        showToast("Category deleted");
        fetchCategories();
      } catch (err) {
        showToast("Error deleting category", "error");
      }
    }
  };

  const addSubcategory = async (catId) => {
    const subName = newSubcategory[catId];
    if (!subName || !subName.trim()) return;
    try {
      await axios.post(`${baseurl}addSubcategory`, {
        category_id: catId,
        name: subName.trim(),
      });
      showToast("Subcategory added");
      setNewSubcategory({ ...newSubcategory, [catId]: "" });
      fetchCategories();
    } catch (err) {
      showToast("Error adding subcategory", "error");
    }
  };

  const updateSubcategory = async (subId) => {
    const updatedName = editSubcategoryName[subId];
    if (!updatedName || !updatedName.trim()) return;
    try {
      await axios.put(`${baseurl}updateSubcategory/${subId}`, { name: updatedName.trim() });
      showToast("Subcategory updated");
      setEditSubcategoryName({ ...editSubcategoryName, [subId]: "" });
      fetchCategories();
    } catch (err) {
      showToast("Error updating", "error");
    }
  };

  const deleteSubcategory = async (subId) => {
    const confirm = await Swal.fire({
      title: "Delete Subcategory?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${baseurl}deleteSubcategory/${subId}`);
        showToast("Deleted");
        fetchCategories();
      } catch (err) {
        showToast("Error", "error");
      }
    }
  };

  const filteredCategories = categories.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.subcategories.some((s) => s.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light" style={{ minHeight: "100vh" }}>
        <div className="main-content p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 className="fw-bold mb-0">Category Builder</h4>
              <p className="text-muted small">Manage your store hierarchy and product tags</p>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <div className="position-relative">
                <FiSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <input
                  type="text"
                  className="form-control ps-5 shadow-sm border-0"
                  style={{ width: "300px", borderRadius: "10px" }}
                  placeholder="Search categories..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Add New Category Section */}
          <div className="card border-0 shadow-sm mb-5 p-4" style={{ borderRadius: "15px" }}>
            <h6 className="fw-bold mb-3"><FiPlus className="me-2" />Add New Category</h6>
            <div className="row g-3 align-items-center">
              <div className="col-md-4">
                <input
                  type="text"
                  className="form-control p-3 bg-light border-0"
                  placeholder="Category Name"
                  style={{ borderRadius: "10px" }}
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <ImageDropzone
                  height="60px"
                  onFileSelect={(file) => setNewCategoryImage(file)}
                  preview={newCategoryImage ? URL.createObjectURL(newCategoryImage) : null}
                  label="Upload Icon"
                />
              </div>
              <div className="col-md-2">
                <button className="btn btn-primary w-100 p-3 fw-bold shadow-sm" style={{ borderRadius: "10px" }} onClick={addCategory}>
                  Create
                </button>
              </div>
            </div>
          </div>

          {/* Grid Layout */}
          <div className="row g-4">
            {filteredCategories.map((c) => (
              <div className="col-xl-4 col-md-6" key={c.id}>
                <div className="card h-100 border-0 shadow-sm category-card" style={{ borderRadius: "15px", overflow: "hidden" }}>
                  {/* Category Header */}
                  <div className="p-3 border-bottom bg-white">
                    <div className="d-flex align-items-center gap-3">
                      <div style={{ width: "50px", height: "50px" }} className="flex-shrink-0">
                        <img
                          src={c.image ? `${imgurl}${c.image}` : "https://via.placeholder.com/50"}
                          className="rounded shadow-sm w-100 h-100 object-fit-cover"
                          alt=""
                        />
                      </div>
                      <div className="flex-grow-1">
                        {activeEditId === c.id ? (
                          <input
                            autoFocus
                            className="form-control form-control-sm mb-1"
                            value={editCategoryName[c.id] ?? c.name}
                            onChange={(e) => setEditCategoryName({ ...editCategoryName, [c.id]: e.target.value })}
                          />
                        ) : (
                          <h6 className="mb-0 fw-bold">{c.name}</h6>
                        )}
                        <span className="text-muted small" style={{ fontSize: "12px" }}>ID: #{c.id}</span>
                      </div>
                      <div className="d-flex gap-1">
                        {activeEditId === c.id ? (
                          <button className="btn btn-sm btn-success rounded-circle p-2" onClick={() => updateCategory(c.id)}><FiCheck /></button>
                        ) : (
                          <button className="btn btn-sm btn-light rounded-circle text-primary" onClick={() => setActiveEditId(c.id)}><FiEdit2 /></button>
                        )}
                        <button className="btn btn-sm btn-light rounded-circle text-danger" onClick={() => deleteCategory(c.id)}><FiTrash2 /></button>
                      </div>
                    </div>

                    {/* Image Update Row (Visible only in edit mode) */}
                    {activeEditId === c.id && (
                      <div className="mt-3 row g-2 align-items-center">
                        <div className="col-8">
                          <ImageDropzone
                            height="50px"
                            label="Change Image"
                            onFileSelect={(f) => setEditCategoryImage({ ...editCategoryImage, [c.id]: f })}
                            preview={editCategoryImage[c.id] ? URL.createObjectURL(editCategoryImage[c.id]) : null}
                          />
                        </div>
                        <div className="col-4">
                          <button className="btn btn-sm btn-outline-secondary w-100 py-2" onClick={() => setActiveEditId(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Subcategories Body */}
                  <div className="card-body bg-light-subtle">
                    <div className="d-flex align-items-center mb-3">
                      <FiFolder className="me-2 text-primary" />
                      <span className="fw-bold small text-uppercase">Subcategories</span>
                    </div>

                    <div className="sub-list mb-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {c.subcategories.map((s) => (
                        <div key={s.id} className="d-flex gap-2 mb-2 align-items-center bg-white p-2 rounded border shadow-xs">
                          <input
                            type="text"
                            className="form-control form-control-sm border-0 bg-transparent"
                            value={editSubcategoryName[s.id] ?? s.name}
                            onChange={(e) => setEditSubcategoryName({ ...editSubcategoryName, [s.id]: e.target.value })}
                          />
                          <button className="btn btn-link btn-sm text-success p-0" onClick={() => updateSubcategory(s.id)} title="Save"><FiCheck /></button>
                          <button className="btn btn-link btn-sm text-danger p-0" onClick={() => deleteSubcategory(s.id)} title="Delete"><FiTrash2 /></button>
                        </div>
                      ))}
                    </div>

                    {/* Quick Add Subcategory */}
                    <div className="input-group input-group-sm mt-auto">
                      <input
                        type="text"
                        className="form-control border-0 bg-white"
                        placeholder="New subcategory..."
                        value={newSubcategory[c.id] || ""}
                        onChange={(e) => setNewSubcategory({ ...newSubcategory, [c.id]: e.target.value })}
                      />
                      <button className="btn btn-primary" onClick={() => addSubcategory(c.id)}>
                        <FiPlus />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {filteredCategories.length === 0 && (
              <div className="col-12 text-center py-5">
                <div className="text-muted">No categories found matching your search.</div>
              </div>
            )}
          </div>
        </div>
      </main>

      <style>{`
        .category-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .category-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }
        .shadow-xs {
           box-shadow: 0 2px 4px rgba(0,0,0,0.02);
        }
        .sub-list::-webkit-scrollbar {
          width: 4px;
        }
        .sub-list::-webkit-scrollbar-thumb {
          background: #ddd;
          border-radius: 10px;
        }
      `}</style>
    </>
  );
}

export default CategoryBuilder;